// Try the voice agent in the browser before a customer calls: a short live
// call over WebRTC with OpenAI Realtime, using the agent's real greeting,
// instructions, voice and tools. Tools run in preview mode on the backend
// (nothing is created: no ticket, no SMS). Backend: POST /api/ai-voice/demo/*.
// Rendered as a card of the agent page (VoiceAgentPage.js).

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Mic, PhoneOff, UserRound, Wrench } from 'lucide-react';
import { Badge, Banner, Card, Meter } from '../../design/ui';
import { fail, previewToolCall, startPreview } from './api';
import { TranscriptTurn } from './VoiceKit';

const VoicePreview = ({ agentId, toolLabel, unsaved = false }) => {
    const { t } = useTranslation();
    const [state, setState] = useState('idle');   // idle | connecting | live | ended
    const [error, setError] = useState('');
    const [lines, setLines] = useState([]);
    const [elapsed, setElapsed] = useState(0);
    const [limit, setLimit] = useState(120);
    const callRef = useRef(null);

    const hangUp = (next = 'ended') => {
        const call = callRef.current;
        callRef.current = null;
        if (call) {
            clearInterval(call.timer);
            try { call.dc?.close(); } catch (e) { /* closed */ }
            try { call.pc?.close(); } catch (e) { /* closed */ }
            call.stream?.getTracks().forEach((track) => track.stop());
            if (call.audio) call.audio.srcObject = null;
        }
        setState(next);
    };

    useEffect(() => () => hangUp('idle'), []);  // eslint-disable-line react-hooks/exhaustive-deps

    const push = (role, text) => {
        if (text) setLines((prev) => [...prev, { role, text }]);
    };

    const onEvent = async (event, session, dc) => {
        if (event.type === 'conversation.item.input_audio_transcription.completed') {
            push('caller', event.transcript);
        } else if (event.type === 'response.output_audio_transcript.done' || event.type === 'response.audio_transcript.done') {
            push('agent', event.transcript);
        } else if (event.type === 'response.function_call_arguments.done') {
            push('tool', toolLabel(event.name));
            let args = {};
            try { args = JSON.parse(event.arguments || '{}'); } catch (e) { args = {}; }
            let output = '';
            try {
                const res = await previewToolCall({ assistant_id: agentId, tool_name: event.name, arguments: args, call_id: session.call_id });
                output = typeof res.data.result === 'string' ? res.data.result : JSON.stringify(res.data.result);
            } catch (err) {
                output = 'The tool is unavailable in this preview.';
            }
            if (dc.readyState !== 'open') return;
            dc.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: event.call_id, output } }));
            if (event.name === 'end_call') {
                setTimeout(() => hangUp(), 2500);
            } else {
                dc.send(JSON.stringify({ type: 'response.create' }));
            }
        } else if (event.type === 'error') {
            setError(event.error?.message || t('voiceSetup.preview.failed', 'L’essai s’est interrompu.'));
        }
    };

    const start = async () => {
        setError('');
        setLines([]);
        setElapsed(0);
        if (!navigator.mediaDevices?.getUserMedia || typeof RTCPeerConnection === 'undefined') {
            setError(t('voiceSetup.preview.unsupported', 'Ce navigateur ne permet pas l’essai vocal : utilisez Chrome, Edge, Firefox ou Safari récent.'));
            return;
        }
        setState('connecting');
        let session;
        try {
            session = (await startPreview(agentId)).data;
        } catch (err) {
            setError(fail(err, t('voiceSetup.preview.startError', 'L’essai n’a pas pu démarrer.')));
            setState('idle');
            return;
        }
        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (err) {
            setError(t('voiceSetup.preview.micDenied', 'Autorisez le micro dans votre navigateur pour parler à l’agent.'));
            setState('idle');
            return;
        }
        try {
            const pc = new RTCPeerConnection();
            const audio = new Audio();
            audio.autoplay = true;
            pc.ontrack = (e) => { audio.srcObject = e.streams[0]; };
            stream.getTracks().forEach((track) => pc.addTrack(track, stream));
            const dc = pc.createDataChannel('oai-events');
            const max = session.max_duration_seconds || 120;
            setLimit(max);
            callRef.current = { pc, dc, stream, audio, timer: null };
            dc.onmessage = (e) => {
                try { onEvent(JSON.parse(e.data), session, dc); } catch (err) { /* ignore malformed */ }
            };
            dc.onopen = () => {
                if (session.greeting) {
                    dc.send(JSON.stringify({ type: 'response.create', response: {
                        instructions: `Start the call by saying exactly, in its language: "${session.greeting}"`,
                    } }));
                }
            };
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            const answer = await fetch(session.realtime_calls_url, {
                method: 'POST',
                body: offer.sdp,
                headers: { Authorization: `Bearer ${session.client_secret}`, 'Content-Type': 'application/sdp' },
            });
            if (!answer.ok) throw new Error(`realtime ${answer.status}`);
            await pc.setRemoteDescription({ type: 'answer', sdp: await answer.text() });
            const started = Date.now();
            callRef.current.timer = setInterval(() => {
                const seconds = Math.floor((Date.now() - started) / 1000);
                setElapsed(seconds);
                if (seconds >= max) hangUp();
            }, 500);
            setState('live');
        } catch (err) {
            hangUp('idle');
            setError(t('voiceSetup.preview.connectError', 'La connexion audio a échoué. Réessayez dans un instant.'));
        }
    };

    const remaining = Math.max(0, limit - elapsed);
    const clock = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`;
    const live = state === 'live';

    let startLabel = t('voiceSetup.preview.start', 'Lancer l’essai');
    if (state === 'connecting') startLabel = t('voiceSetup.preview.connecting', 'Connexion…');
    else if (state === 'ended') startLabel = t('voiceSetup.preview.again', 'Refaire un essai');

    const who = {
        caller: t('voiceSetup.preview.you', 'Vous'),
        agent: t('voiceSetup.preview.agent', 'Agent'),
        tool: t('voiceSetup.preview.tool', 'Outil'),
    };
    const icons = { caller: UserRound, agent: Bot, tool: Wrench };

    return (
        <Card
            icon={Mic}
            title={t('voiceSetup.preview.title', 'Essai dans le navigateur')}
            badge={live ? <Badge tone="success" dot>{t('voiceUi.preview.liveBadge', 'En ligne')}</Badge> : undefined}
            description={live
                ? t('voiceSetup.preview.live', 'En ligne · {{clock}} restantes', { clock })
                : t('voiceSetup.preview.hint', 'Parlez à l’agent avec votre micro, {{minutes}} min maximum. Rien n’est créé : ni ticket, ni SMS.', { minutes: Math.round(limit / 60) })}
            actions={live ? (
                <button type="button" className="u-btn u-btn--danger u-btn--sm" onClick={() => hangUp()}>
                    <PhoneOff aria-hidden="true" />
                    {t('voiceSetup.preview.hangUp', 'Raccrocher')}
                </button>
            ) : (
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={start} disabled={state === 'connecting'}>
                    <Mic aria-hidden="true" />
                    {startLabel}
                </button>
            )}
        >
            {(live || (unsaved && !live) || error || lines.length > 0) && (
                <div className="voice-v2__preview">
                    {live && <Meter value={elapsed} max={limit} label={t('voiceUi.preview.elapsed', 'Durée de l’essai')} />}
                    {unsaved && !live && (
                        <Banner tone="warning">
                            {t('voiceSetup.preview.unsaved', 'L’essai utilise la dernière version enregistrée : enregistrez vos modifications avant d’essayer.')}
                        </Banner>
                    )}
                    {error && <Banner tone="danger">{error}</Banner>}
                    {lines.length > 0 && (
                        <ol className="voice-v2__transcript voice-v2__transcript--compact">
                            {lines.map((line, index) => (
                                // Lines are only ever appended: the position is a stable key.
                                <TranscriptTurn key={index} role={line.role} who={who[line.role]} text={line.text} icon={icons[line.role]} />
                            ))}
                        </ol>
                    )}
                </div>
            )}
        </Card>
    );
};

export default VoicePreview;
